// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching return orders in search/autocomplete components.
 * Uses non-suspense mode to allow showing loading states in the UI.
 */
import { getReturnOrders } from '@/return-order/api/get-return-orders';
import { ReturnOrderFilter } from '@/return-order/constants/status';
import { returnOrderListQueryKey } from '@/return-order/query-keys';
import { REFERENCE_DATA_QUERY_OPTIONS } from '@/utilities/query-profiles';
import { useQuery } from '@tanstack/react-query';

/**
 * Configuration options for the useReturnOrderSearch hook.
 */
interface UseReturnOrderSearchOptions {
  filter?: ReturnOrderFilter;
}

/**
 * React hook for fetching return orders in search components.
 * Uses non-suspense query to enable loading state display.
 *
 * @param options - Configuration object for the hook
 * @returns Object containing returnOrders array and loading state
 */
export function useReturnOrderSearch(
  options: UseReturnOrderSearchOptions = {}
) {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: returnOrderListQueryKey(options.filter),
    queryFn: () => getReturnOrders(options.filter),
    ...REFERENCE_DATA_QUERY_OPTIONS,
  });

  return {
    returnOrders: data?.returnOrders ?? [],
    isLoading,
    isFetching,
  };
}
